import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatCurrencyReadable } from '../../utils/helpers';
import { useCompactMode } from '../../hooks/useCompactMode';
import CompactModeToggle from './CompactModeToggle';

interface AmountInputProps {
    id: string;
    value: string;
    onChange: (value: string) => void;
    /** Placeholder for đ mode (full amounts) */
    placeholder: string;
    /** Placeholder for K mode (thousands) */
    placeholderThousand: string;
    disabled?: boolean;
    required?: boolean;
    className?: string;
}

/**
 * Money field with the đ/K unit switch parked inside it.
 *
 * The unit is the one thing people got wrong about this app: the header switch
 * was the only place that said which unit was active, and nothing pointed at it.
 * Here the active unit sits in the field itself, is tappable, and the line below
 * always says what the typed number is worth — either as a hint before typing,
 * or as the converted amount once there is one.
 */
const AmountInput: React.FC<AmountInputProps> = ({
    id,
    value,
    onChange,
    placeholder,
    placeholderThousand,
    disabled = false,
    required = false,
    className = 'w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none'
}) => {
    const { t } = useTranslation();
    const { compactMode } = useCompactMode();

    // A number typed in K mode means thousands, so the real value is ×1.000
    const enteredAmountVnd = (parseFloat(value) || 0) * (compactMode ? 1000 : 1);
    const readableAmount = formatCurrencyReadable(enteredAmountVnd);

    return (
        <div>
            <div className="relative">
                <input
                    id={id}
                    type="number"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className={`${className} !pr-20`}
                    placeholder={compactMode ? placeholderThousand : placeholder}
                    required={required}
                    aria-required={required}
                    aria-describedby={`${id}-unit-hint`}
                    min="0"
                    step="any"
                    disabled={disabled}
                />
                <CompactModeToggle
                    variant="inline"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                />
            </div>

            <p
                id={`${id}-unit-hint`}
                className="mt-1 text-xs text-gray-500 dark:text-gray-400"
                aria-live="polite"
            >
                {enteredAmountVnd > 0 ? (
                    <span className="italic text-gray-400 dark:text-gray-500">
                        = {formatCurrency(enteredAmountVnd)}
                        {enteredAmountVnd >= 1000 && readableAmount ? ` (${readableAmount})` : ''}
                    </span>
                ) : (
                    compactMode
                        ? t('components.amountField.hintThousand')
                        : t('components.amountField.hintFull')
                )}
            </p>
        </div>
    );
};

export default AmountInput;
